import React, { useMemo, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import BlogPublisherInfo from '@/components/molecules/BlogPublisherInfo/BlogPublisherInfo'
import { BlogBreadCrumbs } from './BlogBreadCrumbs'
import { isNonEmptyArray, isNonEmptyString } from '@/utils/checks'
import { faPlay } from '@fortawesome/free-solid-svg-icons'
import { PlayBtnFill } from 'react-bootstrap-icons'
import {
  isImageBlogHero,
  isVideoBlogHero,
  hasBgColor,
  isVideoBlogHeroWithCoverImage
} from '@/domain/blogPosts/blogHeroChecks'
import { HeroOptions } from '@/types/models/Post.model'
const MAX_TITLE_LENGTH = 50
interface BlogHeroProps {
  blogTitle: string
  isCircleOfChairs: boolean
  isVoices: boolean
  isNews: boolean
  isPodcast: boolean
  authorData: {
    name: string
    image: string
    bio: string
  }
  reviewerData: {
    name: string
    image: string
    bio: string
  }
  publishDate: string
  reviewedDate: string
  heroOptions: HeroOptions
  blogSlug: string
}

const BlogHero = ({
  blogTitle,
  isCircleOfChairs,
  isVoices,
  isNews,
  isPodcast,
  authorData,
  reviewerData,
  publishDate,
  reviewedDate,
  heroOptions,
  blogSlug
}: BlogHeroProps) => {
  const [showCover, setShowCover] = useState(true)
  const [videoSrc, setVideoSrc] = useState('')

  const bgColor = hasBgColor(heroOptions?.themeColor)
    ? heroOptions?.themeColor[0]
    : 'bg-stone-50'
  const mediaContainerClassName = `flex aspect-[3/2] w-full rounded-2xl object-cover md:rounded-3xl lg:max-h-[360px]`
  const fallbackImage = `https://res.cloudinary.com/rehabpath/image/upload/f_auto,q_auto/${blogSlug}`

  useEffect(() => {
    setVideoSrc(heroOptions?.mediaSrc || '')
  }, [heroOptions?.mediaSrc])

  const handleCoverClick = () => {
    const autoplayParam = videoSrc.includes('?') ? '&autoplay=1' : '?autoplay=1'
    setVideoSrc(`${heroOptions?.mediaSrc}${autoplayParam}`)
    setShowCover(false)
  }

  const mediaElement = useMemo(() => {
    if (
      isImageBlogHero({ heroOptions }) &&
      isNonEmptyString(heroOptions?.mediaSrc)
    ) {
      return (
        <img
          className={mediaContainerClassName}
          src={heroOptions.mediaSrc}
          alt={blogTitle}
        />
      )
    }
    if (isVideoBlogHero({ heroOptions }) && !showCover) {
      return (
        <iframe
          title={blogTitle}
          id="blog-hero-video"
          className={mediaContainerClassName}
          src={videoSrc}
          allowFullScreen
        />
      )
    }

    if (
      isVideoBlogHeroWithCoverImage({ heroOptions }) &&
      isNonEmptyString(heroOptions?.coverImage) &&
      showCover
    ) {
      return (
        <div className="relative size-full">
          <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center">
            <button
              className="rounded-full bg-white size-12 lg:size-[72px] shadow-lg hover:bg-gray-200 transition-colors active:bg-gray-300 flex items-center justify-center"
              onClick={handleCoverClick}
              aria-label={`Watch ${blogTitle || 'video'}`}
            >
              <PlayBtn className="text-gray-1100 size-6 lg:size-10" />
            </button>
          </div>
          <div className={`${mediaContainerClassName} cursor-pointer`}>
            <img
              className="size-full object-cover rounded-2xl md:rounded-3xl"
              src={heroOptions.coverImage}
              alt={`${blogTitle} thumbnail`}
            />
          </div>
        </div>
      )
    }

    return (
      <img
        className={mediaContainerClassName}
        src={fallbackImage}
        alt={blogTitle}
      />
    )
  }, [heroOptions, showCover, videoSrc])

  return (
    <div className={`${bgColor} pb-6 pt-4 md:pb-8 md:pt-6 lg:pb-16 lg:pt-10`}>
      <Helmet>
        <link
          rel="preload"
          as={heroOptions?.mediaType?.includes('Video') ? 'video' : 'image'}
          href={heroOptions?.mediaSrc || heroOptions?.coverImage || ''}
          media="(max-height: 360px)"
        />
      </Helmet>
      <div className="mx-auto flex flex-col items-start gap-6 px-4 pb-6 md:px-6 lg:max-w-screen-xl lg:flex-row lg:gap-16 lg:px-8 lg:pb-0 xl:gap-24">
        <div className="flex w-full flex-col gap-3 lg:grow lg:basis-0 lg:gap-6">
          <BlogBreadCrumbs
            isNews={isNews}
            isVoices={isVoices}
            isCircleOfChairs={isCircleOfChairs}
            isPodcast={isPodcast}
            blogTitle={blogTitle}
          />

          <div className="flex w-full flex-col gap-4 lg:gap-6">
            <h1
              // eslint-disable-next-line tailwindcss/no-contradicting-classname
              className={`max-w-[380px] text-balance font-FieldsVariable font-[450] text-gray-1100 md:max-w-[600px] md:font-[375] ${
                blogTitle.length >= MAX_TITLE_LENGTH
                  ? 'text-[28px] leading-8 md:text-[40px] md:leading-[48px]'
                  : 'text-4xl leading-10 md:text-5xl md:leading-[56px]'
              }`}
            >
              {blogTitle}
            </h1>
            <div className="hidden lg:block">
              <BlogPublisherInfo
                authorData={authorData}
                reviewerData={reviewerData}
                publishDate={publishDate}
                reviewedDate={reviewedDate}
              />
            </div>
          </div>
        </div>
        <div className="block w-full grow basis-0">
          <div className={`flex flex-col items-center justify-center`}>
            {mediaElement}
          </div>
        </div>
        <div className="block lg:hidden">
          <BlogPublisherInfo
            authorData={authorData}
            reviewerData={reviewerData}
            publishDate={publishDate}
            reviewedDate={reviewedDate}
          />
        </div>
      </div>
    </div>
  )
}

export default BlogHero

const PlayBtn = ({ className = '' }: { className: string }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="currentColor"
  >
    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393" />
  </svg>
)
